import React, { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { roleOverrideService } from '@/services/roleOverrideService';
import type { ProjectRoleOverride } from '@/types/roleOverride';
import { formatCurrency, formatPercentage } from '@/utils/formatters';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

interface ProjectRoleOverridesTabProps {
  projectId?: number;
}

export const ProjectRoleOverridesTab: React.FC<ProjectRoleOverridesTabProps> = ({ projectId }) => {
  const [overrides, setOverrides] = useState<ProjectRoleOverride[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadOverrides = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await roleOverrideService.getProjectRoleOverrides(projectId);
      setOverrides(data);
    } catch (err) {
      console.error('Error loading project role overrides:', err);
      setError('Failed to load project role overrides');
      toast.error('Failed to load overrides');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadOverrides();
  }, [projectId]);

  const handleDelete = async (id: number, projectName: string, roleName: string) => {
    if (!confirm(`Delete override for ${roleName} on ${projectName}?`)) {
      return;
    }

    try {
      await roleOverrideService.deleteProjectRoleOverride(id);
      toast.success('Override deleted successfully');
      loadOverrides();
    } catch (err) {
      console.error('Error deleting override:', err);
      toast.error('Failed to delete override');
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'Active':
        return <Badge variant="default">Active</Badge>;
      case 'Expired':
        return <Badge variant="secondary">Expired</Badge>;
      case 'Future':
        return <Badge variant="outline">Future</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Project Role Overrides</h3>
          <p className="text-sm text-muted-foreground">
            Custom rates for specific roles on specific projects
          </p>
        </div>
        <Button size="sm">
          <Plus className="h-4 w-4 mr-2" />
          Add Override
        </Button>
      </div>

      {overrides.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <p>No project role overrides found</p>
          <p className="text-sm mt-1">Create an override to set custom rates for a role on a project</p>
        </div>
      ) : (
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Project</TableHead>
                <TableHead>Role</TableHead>
                <TableHead className="text-right">Billing Rate</TableHead>
                <TableHead className="text-right">Cost Rate</TableHead>
                <TableHead className="text-right">Margin</TableHead>
                <TableHead>Effective Period</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {overrides.map((override) => (
                <TableRow key={override.id}>
                  <TableCell className="font-medium">{override.projectName}</TableCell>
                  <TableCell>{override.roleName}</TableCell>
                  <TableCell className="text-right">
                    {override.billingRate ? formatCurrency(override.billingRate, override.currency) : '-'}
                  </TableCell>
                  <TableCell className="text-right">
                    {override.costRate ? formatCurrency(override.costRate, override.currency) : '-'}
                  </TableCell>
                  <TableCell className="text-right">
                    {override.marginPercentage ? formatPercentage(override.marginPercentage) : '-'}
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      <div>From: {new Date(override.effectiveFrom).toLocaleDateString()}</div>
                      {override.effectiveTo && (
                        <div className="text-muted-foreground">
                          To: {new Date(override.effectiveTo).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>{getStatusBadge(override.status)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button variant="ghost" size="sm">
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(override.id, override.projectName, override.roleName)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {overrides.length > 0 && (
        <div className="text-sm text-muted-foreground">
          Showing {overrides.length} override{overrides.length !== 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
};
